import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import SelectReportData from '../../components/Reports/SelectReportData';
import ShowReport from '../../components/Reports/ShowReport';
import CreateReport from '../../components/Reports/CreateReport';
import api from '../../api';
import TrialFilter from '../../components/Filter/TrialFilter';
import { layoutErrorMessage, ownerLabel, parseLayoutFields } from '../../components/Reports/layouts';
import { REPORT_FIELDS } from '../../components/Reports/reportFields';
import ExcelReportButton from '../../components/Reports/ExcelReportButton';
import DataSearchFilters from '../../components/search/DataSearchFilters';
import useDataSearch from '../../hooks/useDataSearch';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';

// Searchable layout list columns (same panel as the master data pages); Status starts at Active
const LAYOUT_SEARCH_FIELDS = [
  { key: 'name', label: 'Name', type: 'text', value: (l) => l.name },
  { key: 'owner', label: 'Owner', type: 'combobox', value: (l) => ownerLabel(l) },
  {
    key: 'status', label: 'Status', type: 'select', value: (l) => (l.active ? 'Active' : 'Inactive'),
    options: [{ value: 'Active' }, { value: 'Inactive' }, { value: '', label: 'All' }], defaultValue: 'Active',
  },
  {
    key: 'visibility', label: 'Visibility', type: 'select', value: (l) => (l.shared ? 'Shared' : 'Private'),
    options: [{ value: '', label: 'All' }, { value: 'Private' }, { value: 'Shared' }],
  },
];

// Field keys in catalog order; reportFields.js has their labels and groups
const fieldOptions = REPORT_FIELDS.map((f) => f.key);

export default function ExistingLayoutFlow() {
  const steps = [
    { id: 1, title: 'Select Layout' },
    { id: 2, title: 'Select Trials' },
    { id: 3, title: 'Show Report' },
  ];

  const [step, setStep] = useState(1);

  // Layouts table state
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [selectedTrialIDs, setSelectedTrialIDs] = useState([]);

  // State for CreateReport modal
  const [showCreateReport, setShowCreateReport] = useState(false);

  // Layout editor (CreateReport modal) state
  const [editingLayoutId, setEditingLayoutId] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [layoutName, setLayoutName] = useState('');
  const [shared, setShared] = useState(false);
  const [layoutFields, setLayoutFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Layout list messages: load/delete errors, and confirmation after a save
  const [listError, setListError] = useState('');
  const [notice, setNotice] = useState('');
  const [reloadLayouts, setReloadLayouts] = useState(false);

  const openLayoutEditor = (layout = null) => {
    setEditingLayoutId(layout?.id ?? null);
    setLayoutName(layout?.name ?? '');
    setShared(Boolean(layout?.shared));
    setIsActive(layout ? layout.active !== false : true);
    setLayoutFields(layout ? parseLayoutFields(layout) : []);
    setSaveError('');
    setShowCreateReport(true);
  };

  const closeLayoutEditor = () => {
    setShowCreateReport(false);
    setEditingLayoutId(null);
    setSaveError('');
  };

  // The editor stays open with the reason shown unless the server confirms the save
  const saveLayout = async () => {
    const name = layoutName.trim();
    if (!name) {
      setSaveError('Enter a name for the layout.');
      return;
    }
    if (layoutFields.length === 0) {
      setSaveError('Select at least one field.');
      return;
    }
    // No owner: the server makes the creator the owner and keeps it on edits
    const payload = { name, active: isActive, shared, definition: { fields: layoutFields } };
    setSaving(true);
    setSaveError('');
    try {
      const res = editingLayoutId
        ? await api.put(`/layouts/${editingLayoutId}`, payload)
        : await api.post('/layouts', payload);
      closeLayoutEditor();
      setNotice(`Layout "${name}" saved.`);
      if (res?.data?.id != null) setSelectedLayout(res.data.id);
      setReloadLayouts((v) => !v);
    } catch (err) {
      setSaveError(layoutErrorMessage(err, 'save'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get('/layouts')
      .then((res) => {
        if (cancelled || !res) return;
        setLayouts(Array.isArray(res.data) ? res.data : []);
        setListError('');
      })
      .catch(() => {
        if (!cancelled) setListError('Could not load the layouts. Please reload the page.');
      });
    return () => { cancelled = true; };
  }, [step, reloadLayouts]);

  const hasSelectedLayout = Boolean(selectedLayout);
  const hasSelectedTrials = selectedTrialIDs.length > 0;

  const canNavigateToStep = (targetStep) => {
    if (targetStep <= step) return true;
    // Select Trials needs no layout: its Excel export does not use one (the report on screen does)
    if (targetStep === 2) return true;
    if (targetStep === 3) return hasSelectedLayout && hasSelectedTrials;
    return false;
  };

  const handleNext = () => {
    const nextStep = step + 1;
    if (nextStep <= steps.length && canNavigateToStep(nextStep)) {
      setStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (step > 1) setStep(step - 1);
  };

  const [hideEmptyTaskRows, setHideEmptyTaskRows] = useState(true);


  // The layout being deleted (confirmed in ConfirmDeleteDialog by typing its name)
  const [layoutToDelete, setLayoutToDelete] = useState(null);
  const handleDeleteLayout = async (id) => {
    setNotice('');
    try {
      await api.delete(`/layouts/${id}`);
      setListError('');
      if (selectedLayout === id) setSelectedLayout(null);
    } catch (err) {
      setListError(layoutErrorMessage(err, 'delete'));
    }
    setReloadLayouts((v) => !v);
  };

  const selectedLayoutFields = parseLayoutFields(layouts.find((l) => l.id === selectedLayout));
  const { filtered: visibleLayouts, panelProps: layoutSearchProps } = useDataSearch(layouts, LAYOUT_SEARCH_FIELDS);

  // State for showing the filter pop-up
  const [showTrialFilter, setShowTrialFilter] = useState(false);

  // Example: handle filter result
  const handleTrialFilterOk = () => {
    // Do something with filterValues (JSON with only set values)
    setShowTrialFilter(false);
    // You can store filterValues in state if needed
    // setTrialFilterValues(filterValues);
  };

  return (
    <Layout>
      <ConfirmDeleteDialog
        open={!!layoutToDelete}
        title="Delete layout"
        name={layoutToDelete?.name}
        onDelete={() => handleDeleteLayout(layoutToDelete.id)}
        onClose={() => setLayoutToDelete(null)}
        onDone={() => setLayoutToDelete(null)}
      />
      <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow p-8">
        {/* Progress Bar */}
        <nav className="space-y-2 mb-6">
          <div className="flex h-1 w-full items-center overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-900">
            <div
              role="progressbar"
              aria-valuenow={(step / steps.length) * 100}
              aria-valuemin="0"
              aria-valuemax="100"
              className="flex items-center justify-center self-stretch rounded-full bg-emerald-600 text-xs font-medium text-white transition-all duration-500 ease-out"
              style={{ width: `${(step / steps.length) * 100}%` }}
            ></div>
          </div>
          <ol className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4" role="list">
            {steps.map((s) => (
              <li key={s.id}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => {
                      if (canNavigateToStep(s.id)) {
                        setStep(s.id);
                      }
                    }}
                    disabled={!canNavigateToStep(s.id)}
                    className={`flex items-center justify-between gap-2 rounded-lg border px-4 py-2 text-sm leading-6 transition-all duration-300 ${
                      step === s.id
                        ? 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-700/50 dark:text-gray-300'
                        : canNavigateToStep(s.id)
                          ? 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700'
                          : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 opacity-70 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500'
                    }`}
                  >
                    <div className="grow font-semibold">{s.title}</div>
                    {step > s.id && (
                      <div className="flex-none text-emerald-600">
                        <svg
                          className="inline-block size-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 6.292a1 1 0 00-1.408-1.416l-5.3 5.28-2.3-2.3a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l6-5.978z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </nav>

        {/* Step Content */}
        <div className="mb-6">
          {step === 1 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Select Layout</h2>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => openLayoutEditor()}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md transition-colors duration-200"
                  >
                    Create Layout
                  </button>
                </div>
              </div>
              <DataSearchFilters {...layoutSearchProps} />
              {listError && (
                <div role="alert" className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                  {listError}
                </div>
              )}
              {notice && (
                <div role="status" className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
                  {notice}
                </div>
              )}
              {showCreateReport && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                  onKeyDown={(e) => { if (e.key === 'Escape') closeLayoutEditor(); }}
                >
                  {/* At most the screen's height: the content scrolls inside, and the close button stays in
                      view on a low screen (customer feedback 8) */}
                  <div role="dialog" aria-label={editingLayoutId ? 'Edit layout' : 'Create layout'} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col relative">
                    <button
                      onClick={closeLayoutEditor}
                      aria-label="Close"
                      className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-xl text-gray-500 shadow hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                    >
                      &times;
                    </button>
                    <div className="overflow-y-auto p-8" data-testid="layout-dialog-body">
                    <CreateReport
                      isActive={isActive}
                      setIsActive={setIsActive}
                      layoutName={layoutName}
                      setLayoutName={setLayoutName}
                      saveLayout={saveLayout}
                      saving={saving}
                      error={saveError}
                      shared={shared}
                      setShared={setShared}
                      ownerName={ownerLabel(layouts.find((l) => l.id === editingLayoutId))}
                      fields={layoutFields}
                      onFieldsChange={setLayoutFields}
                      fieldOptions={fieldOptions}
                      editing={editingLayoutId != null}
                    />
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700/50">
                      <th className="bg-gray-100/75 px-3 py-4 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Name</th>
                      <th className="bg-gray-100/75 px-3 py-4 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Owner</th>
                      <th className="bg-gray-100/75 px-3 py-4 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Active</th>
                      <th className="bg-gray-100/75 px-3 py-4 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Visibility</th>
                      <th className="bg-gray-100/75 px-3 py-4 text-right font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Fields</th>
                      <th className="bg-gray-100/75 px-3 py-4 text-center font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLayouts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                          {layouts.length === 0 ? 'No layouts found.' : 'No layouts match the search.'}
                        </td>
                      </tr>
                    ) : (
                      visibleLayouts.map((layout) => (
                        <tr
                          key={layout.id}
                          className={`border-b border-gray-100 dark:border-gray-700/50 cursor-pointer transition-colors ${
                            selectedLayout === layout.id
                              ? 'bg-emerald-100 dark:bg-emerald-900/30'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700/30'
                          }`}
                          onClick={() => setSelectedLayout(layout.id)}
                        >
                          <td className="px-3 py-2">{layout.name}</td>
                          <td className="px-3 py-2">{ownerLabel(layout) || '—'}</td>
                          <td className="px-3 py-2">
                            {layout.active ? (
                              <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-900 dark:bg-emerald-700/10 dark:text-emerald-200">Active</span>
                            ) : (
                              <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-700 dark:bg-gray-700/20 dark:text-gray-400">Inactive</span>
                            )}
                          </td>
                          <td className="px-3 py-2">{layout.shared ? 'Shared' : 'Private'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{parseLayoutFields(layout).length}</td>
                          <td className="px-3 py-2 text-center flex gap-2 justify-center">
                            {layout.can_edit && (<>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openLayoutEditor(layout);
                              }}
                              className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                setLayoutToDelete(layout);
                              }}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                            >
                              Delete
                            </button>
                            </>)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <SelectReportData onSelectTrials={setSelectedTrialIDs} selectedTrialIDs={selectedTrialIDs} selectedLayoutId={selectedLayout} />
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold mb-4">Show Report</h2>
              {/* The customer's weekly file for the whole selection: All data, this layout's sheet, Colors */}
              <div className="mb-4 flex justify-end">
                <ExcelReportButton trialIds={selectedTrialIDs} layout={layouts.find((l) => l.id === selectedLayout) ?? null} />
              </div>
              <ShowReport
                trialIDs={selectedTrialIDs}
                fields={selectedLayoutFields}
                hideEmptyTaskRows={hideEmptyTaskRows}
                onHideEmptyTaskRowsChange={setHideEmptyTaskRows}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {step === 1 && !hasSelectedLayout && 'Select a layout to show the report on screen and export it to Excel.'}
            {step === 2 && !hasSelectedTrials && 'Select at least one trial to show the report and export it.'}
            {step === 2 && hasSelectedTrials && !hasSelectedLayout && 'Select a layout on step 1 to show the report and export it to Excel.'}
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={step === 1}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            {/* The report is the last step: no Next there */}
            {step < steps.length && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNavigateToStep(step + 1)}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 dark:disabled:bg-emerald-800"
              >
                Next: {steps[step].title}
              </button>
            )}
          </div>
        </div>

        {/* TrialFilter pop-up */}
        {showTrialFilter && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-xl relative">
              <button
                onClick={() => setShowTrialFilter(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                title="Close"
              >
                &times;
              </button>
              <TrialFilter onOk={handleTrialFilterOk} />
            </div>
          </div>
        )}

        {/* ...rest of the component unchanged... */}
      </div>
    </Layout>
  );
}
